import { Images3D, NotificationService, useIntl, useLocale } from '@capsicum/common';
import {
	ActivityIndicator,
	Alert,
	BaseTextInput,
	Button,
	GenericDrawer,
	Image,
	Image3D,
	ModalService,
	Snackbar,
	useRouter
} from '@capsicum/common-web';
import { InfoIcon } from '@capsicum/common-web/icons';
import {
	type AxiosErrorData,
	REDEEM_LIMIT_ERROR_CODE,
	useRPAuctionDetails,
	useRPBid
} from '@capsicum/features/fanshop/shared';
import { useMe, useNCWBalance } from '@capsicum/socios-core';
import { useAtom } from 'jotai';
import { type ChangeEvent, useCallback, useMemo, useState } from 'react';
import styled, { useTheme } from 'styled-components';

import { AuctionBidConfirmDrawer } from '../../ui';
import { isBidWarningViewedAtom } from '../../ui/auction-bid-confirm-drawer/AuctionBidConfirmDrawer';

type BidPageProps = {
	auctionId: string;
};

export const RPBid = ({ auctionId }: BidPageProps) => {
	const { $t } = useIntl();
	const { colors } = useTheme();
	const { locale } = useLocale();
	const { me } = useMe();
	const router = useRouter();
	const [activeImage, setActiveImage] = useState<number>(0);
	const [isBidWarningViewed] = useAtom(isBidWarningViewedAtom);

	const onThumbnailChange = (index: number) => {
		setActiveImage(index);
	};

	const { getTokenBalanceBy } = useNCWBalance();

	const {
		allImages,
		auctionData,
		availableRPBalance,
		hasEnoughSsu,
		isActive,
		isBidAllowed,
		isBidEnabled,
		isSkeletonLoading,
		nextBid
	} = useRPAuctionDetails({
		auctionId: auctionId || '',
		getTokenBalanceBy
	});

	const { amount, isPostRPBidPending, postRPBidMutateAsync, setAmount, texts, validateAmount } = useRPBid({
		mainCurrencyShortcode: 'RP',
		minimumBid: nextBid,
		userAmount: availableRPBalance || 0
	});

	const onPendingBid = useCallback(
		() =>
			NotificationService.addNotification({
				component: (
					<Snackbar
						description={$t('rewards.rpAuctions.bid.processingMessage')}
						testID="auctionDetails-pendingBid"
						title={$t('rewards.rpAuctions.bid.processingTitle')}
						variant="pending"
					/>
				)
			}),
		[$t]
	);

	const onRedeemLimitErrorImage = useMemo(() => {
		return <Image3D image={Images3D.warning} testID="auction-drawer-redeemLimitExceededImage" />;
	}, [Images3D.warning]);

	const onRedeemLimitError = useCallback(() => {
		ModalService.addModal({
			detached: true,
			isClosable: false,
			renderComponent: ({ close }) => (
				<GenericDrawer
					buttonText={texts.redeemLimitExceededPrimaryButton}
					description={texts.redeemLimitExceededDescription}
					image={onRedeemLimitErrorImage}
					isButtonOnRow={false}
					isExternalLink
					secondaryActionButtonText={texts.redeemLimitExceededSecondaryButton}
					testID="auction-drawer-redeemLimitExceeded"
					title={texts.redeemLimitExceededTitle}
					onButtonPress={() => {
						close();
						router.push('/fan-shop/rewards');
					}}
					onSecondaryActionOnButtonPress={() => {
						window?.open?.(texts.legalHubUrl, '_blank')?.focus();
					}}
				/>
			)
		});
	}, [onRedeemLimitErrorImage, router, texts]);

	const onFailedBid = useCallback(
		(errorsObj?: Array<AxiosErrorData> | undefined) => {
			if (errorsObj) {
				// Check for redeem limit error first - if found, handle it and exit early
				const hasRedeemLimitError = errorsObj?.some(
					(error: AxiosErrorData) => error?.code === REDEEM_LIMIT_ERROR_CODE
				);

				if (hasRedeemLimitError) {
					onRedeemLimitError();

					return;
				}
				// Process other errors only if no redeem limit error was found
				errorsObj?.forEach((error: AxiosErrorData) => {
					const message = error?.translations?.[locale] || error?.translations?.[0];
					const title = $t('rewards.rpAuctions.toasts.error.errorCode') + error?.status;
					const genericMessage = $t('rewards.rpAuctions.toasts.error.unhandledCode.message');
					const genericTitle = $t('rewards.rpAuctions.toasts.error.unhandledCode.title');

					return NotificationService.addNotification({
						component: (
							<Snackbar
								description={error?.status === 400 ? message : genericMessage}
								testID="auctionDetails-failedBid"
								title={error?.status === 400 ? title.toString() : genericTitle}
								variant="error"
							/>
						)
					});
				});
			}
		},
		[$t, locale, onRedeemLimitError]
	);

	const postBid = (auctionId: string, amount: number) => {
		if (me?.vatCountry) {
			postRPBidMutateAsync(
				{
					amount: Number(amount).toFixed(18),
					auctionId: auctionId,
					countryCode: me?.vatCountry
				},
				{
					onSuccess: () => {
						onPendingBid();
						router.back();
					}
				}
			).catch(error => {
				if (error?.response?.data?.errors[0]) {
					onFailedBid(error.response.data.errors);
				}
			});
		}
	};

	const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
		const text = e.target.value;
		const regex = /^[0-9\b]+$/;

		if (text === '' || regex.test(text)) {
			setAmount(Number(text));
		}
	};

	const button = useMemo(
		() =>
			isActive &&
			isBidAllowed && (
				<S.ButtonWrapper>
					<Button
						color="brand"
						disabled={
							!hasEnoughSsu || !isBidEnabled || !!validateAmount(amount, nextBid, availableRPBalance || 0)
						}
						isLoading={isSkeletonLoading || isPostRPBidPending}
						testID="auction-button-place-bid-confirm"
						text={texts.placeABid}
						onClick={() => {
							isBidWarningViewed
								? postBid(auctionId, amount)
								: ModalService.addModal({
										detached: true,
										isClosable: true,
										renderComponent: ({ close }) => (
											<AuctionBidConfirmDrawer
												testID="auction-dialog-place-bid-confirm"
												onButtonPress={() => {
													postBid(auctionId, amount);
													close();
												}}
											/>
										)
									});
						}}
					/>
				</S.ButtonWrapper>
			),
		[
			isActive,
			texts.placeABid,
			isPostRPBidPending,
			isSkeletonLoading,
			isBidAllowed,
			isBidEnabled,
			amount,
			hasEnoughSsu
		]
	);

	const imageWrapper = useMemo(
		() => (
			<S.ImageWrapper>
				<S.ImageContainer>
					{allImages?.[activeImage]?.url ? (
						<S.ProductImage
							alt={`${auctionData?.label} image`}
							blurhash={allImages?.[activeImage]?.blurHash}
							height={303}
							optimized
							src={allImages?.[activeImage]?.url || ''}
							width={256}
						/>
					) : (
						<S.StyledCardImagePlaceholder />
					)}
				</S.ImageContainer>
				{allImages?.length > 1 && (
					<S.GalleryView>
						{allImages?.map((image, index) => (
							<S.Pressable
								$selected={activeImage === index}
								data-testid={`auction-galleryView-image${index}`}
								key={index}
								onClick={() => onThumbnailChange(index)}
							>
								<S.StyledImage
									alt={`${auctionData?.label ?? 'default-label'}-image${index}`}
									blurhash={image?.blurHash}
									height={70}
									optimized
									src={image?.url || ''}
									width={90}
								/>
							</S.Pressable>
						))}
					</S.GalleryView>
				)}
			</S.ImageWrapper>
		),
		[allImages, activeImage, allImages?.[activeImage]?.url]
	);

	return (
		<S.PageWrapper>
			<S.Title>{texts.enterBid}</S.Title>
			<S.MainContainer>
				{imageWrapper}
				<S.Content>
					{isSkeletonLoading ? (
						<S.Loading>
							<ActivityIndicator color={colors.icons.main.enabled} />
						</S.Loading>
					) : (
						<>
							<BaseTextInput
								componentEnd={<S.MainCurrencyShortcode>{texts.rewardPoints}</S.MainCurrencyShortcode>}
								error={validateAmount(amount, nextBid, availableRPBalance || 0)}
								testID="auctionPageDrawer-bidAmount-text"
								value={amount.toString()}
								onChange={onInputChange}
							/>
							<Alert
								closable={false}
								icon={InfoIcon}
								subTitle={texts.warning}
								testID="placeBid-auction-warning"
								title=""
								type="important"
							/>
						</>
					)}

					{button}
				</S.Content>
			</S.MainContainer>
		</S.PageWrapper>
	);
};

namespace S {
	export const PageWrapper = styled.div`
		display: flex;
		flex: 1;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.large};

		${({ theme }) => theme.breakpoints.queries.only.mobile} {
			margin-bottom: ${({ theme }) => theme.sizing.xxlarge};
		}
	`;

	export const ImageWrapper = styled.div`
		display: flex;
		flex-direction: column;
		display: flex;
		flex: 1 0 0;
	`;

	export const ImageContainer = styled.div`
		width: 100%;
		max-width: 100%;
		${({ theme }) => theme.breakpoints.queries.only.mobile} {
			margin-bottom: ${({ theme }) => theme.spacings.medium};
		}
	`;

	export const StyledCardImagePlaceholder = styled.div`
		height: 254px;
		width: 100%;
		background-color: ${({ theme }) => theme.colors.skeleton.default};
	`;

	export const StyledImage = styled(Image)`
		object-fit: contain;
	`;

	export const Pressable = styled.div<{ $selected: boolean }>`
		margin-right: ${({ theme }) => theme.spacings.xxsmall};
		border: ${({ $selected, theme }) =>
			$selected
				? `${theme.border.width.large} solid ${theme.colors.borders.brand.selected}`
				: `${theme.border.width.large} solid ${theme.colors.backgrounds.transparent}`};
	`;

	export const ProductImage = styled(Image)`
		object-fit: contain;
		width: 100%;
		margin: 0 auto;
		${({ theme }) =>
			theme.breakpoints.queries.builder.or(
				theme.breakpoints.queries.above.mediumDesktop,
				theme.breakpoints.queries.only.mobile
			)} {
		}
	`;

	export const GalleryView = styled.div`
		display: flex;
		width: 100%;
		overflow: hidden;
		margin-top: ${({ theme }) => theme.spacings.medium};
		&:hover {
			overflow-x: scroll;
		}
		${({ theme }) =>
			theme.breakpoints.queries.builder.or(
				theme.breakpoints.queries.above.tabletSmallDesktop,
				theme.breakpoints.queries.only.mobile
			)} {
			&:hover {
				overflow-x: unset;
			}
		}
		${({ theme }) => theme.breakpoints.queries.only.mobile} {
			margin-top: 0;
		}
	`;

	export const Content = styled.div`
		display: flex;
		flex: 1 0 0;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.small};
	`;

	export const MainContainer = styled.div`
		display: flex;
		gap: ${({ theme }) => theme.spacings.small};
		padding: ${({ theme }) => theme.spacings.small};
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		${({ theme }) => theme.border.radius.medium};

		${({ theme }) => theme.breakpoints.queries.below.mobile} {
			flex-direction: column;
		}
	`;

	export const MainCurrencyShortcode = styled.span`
		${({ theme }) => theme.fonts.text.text4};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		align-self: center;
		flex: 1 0 auto;
	`;

	export const Title = styled.span`
		${({ theme }) => theme.fonts.headings.title5};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		margin: ${({ theme }) => theme.spacings.xxlarge} 0 0;
		${({ theme }) => theme.breakpoints.queries.below.tabletSmallDesktop} {
			margin-left: ${({ theme }) => theme.spacings.small};
		}
	`;

	export const ButtonWrapper = styled.div`
		display: flex;
		align-self: flex-end;
		align-items: center;

		${({ theme }) => theme.breakpoints.queries.below.mobile} {
			position: fixed;
			display: grid;
			bottom: 0;
			left: 0;
			right: 0;
			z-index: 10;
			padding: ${({ theme }) => `${theme.spacings.xsmall} ${theme.spacings.small}`};
			background: ${({ theme }) => theme.colors.backgrounds.main.translucent};
			border-top: ${({ theme }) => `1px solid ${theme.colors.borders.main.divider}`};
			backdrop-filter: blur(2px);
		}
	`;

	export const Loading = styled.div`
		display: flex;
		justify-content: center;
		padding: ${({ theme }) => `${theme.spacings.medium}`};
		margin: auto;
	`;
}
